/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Components/EnhancedDropdown" />
import * as React from "react";
import * as Resources from "Build/DesignerExtensions/YamlSnippetForm/Resources";

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { FormItem } from "VSSUI/FormItem";
import { EditableDropdown, IEditableDropdownProps } from "VSSUI/EditableDropdown";
import { Dropdown } from "VSSUI/Dropdown";
import { IListSelection } from "VSSUI/List";
import { DropdownMultiSelection, DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IListBoxItem, ListBoxItemType, LoadingCell } from "VSSUI/ListBox";
import { ITableColumn } from "VSSUI/Table";

import { EnhancedLabel } from "Build/DesignerExtensions/YamlSnippetForm/Components/EnhancedLabel";
import { YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";

export interface IEnhancedDropdownProps {
    inputDefinition: YamlSnippetInput;
    onValueChanged(name: string, value: string, valueAlias?: string): void;
    getValue(name: string): string;
    useValueAliases?: boolean;

    loadItems?(): Promise<IListBoxItem[]>;
    loadOnMount?: boolean;
    alwaysRefresh?: boolean;
    onError?(location: string, error: Error): void;
    errorContext?: string;
}

export class EnhancedDropdown extends React.PureComponent<IEnhancedDropdownProps, {}> {
    private _selection: IListSelection;
    private _allowMultiselection: boolean;
    private _editable: boolean;
    private _items = new ObservableArray<IListBoxItem>();
    private _message = new ObservableValue<string | undefined>(undefined);
    private _error = new ObservableValue<boolean>(false);
    private _initialValues: string;

    private _loadingItem: IListBoxItem = {
        id: "loading",
        type: ListBoxItemType.Loading,
        render: (
            rowIndex: number,
            columnIndex: number,
            tableColumn: ITableColumn<IListBoxItem<{}>>,
            tableItem: IListBoxItem<{}>
        ) => {
            return (
                <LoadingCell
                    key={rowIndex}
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                    tableItem={tableItem}
                    onMount={this._onLoadingMount}
                />
            );
        }
    };

    constructor(props: IEnhancedDropdownProps) {
        super(props);

        this._allowMultiselection = allowsMultiSelect(props.inputDefinition.properties);
        this._editable = allowEditable(props.inputDefinition.properties);
        if (this._allowMultiselection) {
            // Design System Dropdown does not currently support multi selection + editable
            this._editable = false;
        }

        this._selection = this._allowMultiselection && new DropdownMultiSelection() || new DropdownSelection();
        this._initialValues = props.getValue(props.inputDefinition.name);

        if (props.loadItems) {
            this._items.push(this._loadingItem);
        }
        else {
            let options = Object.keys(props.inputDefinition.options).map(id => { return { id, text: props.inputDefinition.options[id] } });
            let startingValues = props.getValue(this.props.inputDefinition.name);
    
            if (startingValues !== "") {
                const startingValuesArray = this._allowMultiselection ? startingValues.split(",") : [startingValues];
                startingValuesArray.forEach(startingValue => {
                    startingValue = startingValue.trim();
                    if(options.find(option => option.id === startingValue) === undefined) {
                        options.push({
                            id: startingValue,
                            text: startingValue
                        });
                    }

                    const index = options.findIndex(option => option.id === startingValue);
                    if (index >= 0) {
                        this._selection.select(index);
                    }
                });
            }
            else {
                this.props.inputDefinition.defaultValue
            }
    
            this._items.change(0, ...options);
        }
    }

    public render(): JSX.Element {
        const inputDefinition = this.props.inputDefinition;

        const commonProps = {
            noItemsText: Resources.NoResultsFound,
            items: this._items,
            selection: this._selection,
            onExpand: this._onExpand,
            placeholder: this.props.loadItems && this._initialValues,
            required: inputDefinition.required
        } as IEditableDropdownProps;

        return <FormItem message={this._message} error={this._error} label={<EnhancedLabel title={inputDefinition.label} isRequired={inputDefinition.required} documentation={inputDefinition.helpMarkdown}/>} >
                {this._editable && 
                    <EditableDropdown
                        {...commonProps}
                        allowFreeform={true}
                        onValueChange={this._onValueChange} />}
                {!this._editable && 
                    <Dropdown 
                        {...commonProps}
                        onSelect={this._onSelect} />}
        </FormItem>
    }

    public componentDidMount() {
        if (this.props.loadOnMount && this.props.loadItems) {
            this._onLoadingMount();
        }
    }

    // non editable potentially multi-selection
    private _onSelect = () => {
        let selectedValues: string[] = [];
        let selectedNames: string[] = [];

        for (let i = 0; i < this._selection.value.length; i++) {
            let beginIndex = this._selection.value[i].beginIndex;
            let endIndex = this._selection.value[i].endIndex;

            for (let j = beginIndex; j <= endIndex; j++) {
                selectedValues.push(this._items.value[j].id);
                selectedNames.push(this._items.value[j].text!);
            }
        }

        this._onValueChangeInternal(selectedValues.join(", "), selectedNames.join(", "));
    }

    // editable, no multi selection
    private _onValueChange = (value?: IListBoxItem) => {
        if (value) {
            const selectedItem = this._items.value.find(i => i.id === value.id)
            if (selectedItem) {
                this._onValueChangeInternal(selectedItem.id, selectedItem.text || selectedItem.id);
            }
            else {
                this._onValueChangeInternal(value.id, value.id);
            }
        }
    }
    
    private _onValueChangeInternal(value: string, valueAlias: string) {
        this.props.onValueChanged(this.props.inputDefinition.name, value, this.props.useValueAliases && valueAlias || undefined);
    }

    private _onLoadingMount = async () => {
        try {
            this._items.value = await this.props.loadItems!();

            this._error.value = false;
            this._message.value = undefined;
            this._selection.clear();

            // If the user provided an option for this that we didn't find a match for it, let them still pick the one they had
            if (this._initialValues) {
                const initialValues = this._allowMultiselection ? this._initialValues.split(",") : [this._initialValues];
                initialValues.forEach(initialValue => {
                    initialValue = initialValue.trim();
                    if (!this._items.value.find(item => item.text === initialValue || item.id === initialValue)) {
                        this._items.push({
                            id: initialValue,
                            text: initialValue
                        })
                    }
                });
            }

            const currentValue = this.props.getValue(this.props.inputDefinition.name);
            if (currentValue) {
                const currentValueArray = this._allowMultiselection ? currentValue.split(",") : [currentValue];
                currentValueArray.forEach(value => {
                    value = value.trim();
                    const index = this._items.value.findIndex(option => option.text === value || option.id === value);
                    if (index >= 0) {
                        this._selection.select(index);
                    }
                });
            }
        } catch (error) {
            this._items.value = [];
            this._error.value = true;
            this._message.value = error.message;
            this._selection.clear();

            this.props.onError && this.props.onError(this.props.errorContext || "", error);
        }        
    }

    private _onExpand = () => {
        if (this.props.loadItems && (this._error.value || this.props.alwaysRefresh)) {
            this._items.removeAll();
            this._items.push(this._loadingItem);
        }
    }
}

export function allowsMultiSelect(properties: { [key: string] : string; }): boolean {
    if (properties) {
        if (properties.MultiSelectFlatList && properties.MultiSelectFlatList.toLowerCase() === "true") {
            return true;
        }

        if (properties.MultiSelect && properties.MultiSelect.toLowerCase() === "true") {
            return true;
        }
    }

    return false;
}

export function allowEditable(properties: { [key: string] : string; }): boolean {
    if (properties && properties.EditableOptions && properties.EditableOptions.toLowerCase() === "true") {
        return true;
    }

    return false;
}